/**
 * SmartIntake — Public page for targeted patient intake
 *
 * Route: /intake/smart/:token
 *
 * Flow:
 *  1. Validate token → get missingFields, therapyCategory, providerName, clinicName
 *  2. Render only the missing fields grouped by category
 *  3. Submit responses to POST /api/intakes/submit/:token
 *  4. Show confirmation
 */

import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  AlertCircle,
  CheckCircle2,
  ClipboardList,
  Heart,
  Brain,
  User,
  Loader2,
  Shield,
  Package,
} from 'lucide-react'
import ShippingBillingAddressStep, {
  type AddressFields,
  initialAddressFields,
  isAddressComplete,
} from '@/components/intake/ShippingBillingAddressStep'
import { getIntakePrefillData } from '@/services/intakeService'

// ─────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────

interface MissingField {
  fieldId: string
  /** Backend may send either key */
  name?: string
  fieldName?: string
  priority: 'required' | 'recommended' | 'optional'
  category: string
  description?: string
}

interface ValidateData {
  intakeId: string
  patientId: string
  intakeType: string
  therapyCategory: string
  missingFields: MissingField[]
  providerName: string
  clinicName: string | null
  patientName: string | null
  expiresAt: string
}

type PageState = 'loading' | 'error' | 'form' | 'submitting' | 'submitted'

// ─────────────────────────────────────────────
// Static field metadata (label, input type, options)
// ─────────────────────────────────────────────

interface FieldMeta {
  label: string
  hint?: string
  inputType: 'textarea' | 'number' | 'select' | 'text' | 'bmi_calculator'
  options?: { value: string; label: string }[]
}

const FIELD_META: Record<string, FieldMeta> = {
  symptoms_assessment: {
    label: 'Symptom Assessment',
    hint: 'Describe your current symptoms in detail',
    inputType: 'textarea',
  },
  chief_complaint: {
    label: 'Chief Complaint',
    hint: 'What is the main reason for your visit?',
    inputType: 'textarea',
  },
  goals: {
    label: 'Health Goals',
    hint: 'What would you like to achieve with treatment?',
    inputType: 'textarea',
  },
  medical_history: {
    label: 'Medical History',
    hint: 'List any relevant conditions, surgeries, or health events',
    inputType: 'textarea',
  },
  weight: {
    label: 'Weight (lbs)',
    inputType: 'number',
  },
  bmi: {
    label: 'BMI',
    hint: 'Enter your height and weight — BMI is calculated automatically',
    inputType: 'bmi_calculator',
  },
  stop_bang: {
    label: 'STOP-Bang Score (0–8)',
    hint: 'Your physician will calculate this if unknown — enter your best estimate',
    inputType: 'number',
  },
  gender: {
    label: 'Gender Identity',
    inputType: 'select',
    options: [
      { value: 'male', label: 'Male' },
      { value: 'female', label: 'Female' },
      { value: 'non_binary', label: 'Non-binary' },
      { value: 'prefer_not_to_say', label: 'Prefer not to say' },
    ],
  },
  menopausal_status: {
    label: 'Menopausal Status',
    inputType: 'select',
    options: [
      { value: 'pre', label: 'Pre-menopausal' },
      { value: 'peri', label: 'Peri-menopausal' },
      { value: 'post', label: 'Post-menopausal' },
      { value: 'unknown', label: 'Not sure' },
    ],
  },
  athlete_status: {
    label: 'Athletic Activity Level',
    inputType: 'select',
    options: [
      { value: 'sedentary', label: 'Sedentary (little to no exercise)' },
      { value: 'recreational', label: 'Recreational (1–3 days/week)' },
      { value: 'active', label: 'Active (3–5 days/week)' },
      { value: 'athlete', label: 'Athlete (daily intense training)' },
    ],
  },
}

function getFieldMeta(fieldId: string, field: MissingField): FieldMeta {
  if (FIELD_META[fieldId]) return FIELD_META[fieldId]
  // Derive label from field name
  const label = field.name || field.fieldName || fieldId.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
  return { label, inputType: 'text' }
}

// ─────────────────────────────────────────────
// Category metadata
// ─────────────────────────────────────────────

const CATEGORY_META: Record<string, { icon: React.ElementType; label: string }> = {
  symptoms: { icon: Heart, label: 'Symptoms' },
  medical_history: { icon: Brain, label: 'Medical History' },
  questionnaire: { icon: ClipboardList, label: 'Questionnaires' },
  demographics: { icon: User, label: 'Demographics' },
}

const CATEGORY_ORDER: Record<string, number> = {
  symptoms: 1,
  questionnaire: 2,
  medical_history: 3,
  demographics: 4,
}

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3001/api/'

function apiUrl(path: string): string {
  const base = API_BASE.endsWith('/') ? API_BASE : `${API_BASE}/`
  return `${base}${path.startsWith('/') ? path.slice(1) : path}`
}

function therapyLabel(category: string): string {
  const map: Record<string, string> = {
    trt: "Men's Health (TRT)",
    hrt: "Women's Health (HRT)",
    glp1: 'Weight Management (GLP-1)',
    peptide: 'Peptide Therapy',
    nad: 'NAD+ Therapy',
    fertility: 'Fertility',
    general: 'General Health',
  }
  return map[category] ?? category
}

// ─────────────────────────────────────────────
// Progress bar
// ─────────────────────────────────────────────

const ProgressBar: React.FC<{ filled: number; total: number }> = ({ filled, total }) => {
  const pct = total > 0 ? Math.round((filled / total) * 100) : 0
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs text-slate-400">
        <span>{filled} of {total} answered</span>
        <span>{pct}%</span>
      </div>
      <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden">
        <div
          className="h-full bg-[#00A79D] transition-all duration-300 rounded-full"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────
// BMI Calculator field
// ─────────────────────────────────────────────

function calculateBmi(heightFeet: number, heightInches: number, weightLbs: number): number | null {
  const totalInches = heightFeet * 12 + heightInches
  if (totalInches <= 0 || weightLbs <= 0) return null
  return Math.round((weightLbs * 703) / (totalInches * totalInches) * 10) / 10
}

function getBmiCategory(bmi: number): { label: string; color: string } {
  if (bmi < 18.5) return { label: 'Underweight', color: 'text-blue-400' }
  if (bmi < 25) return { label: 'Healthy', color: 'text-emerald-400' }
  if (bmi < 30) return { label: 'Overweight', color: 'text-amber-400' }
  if (bmi < 35) return { label: 'Obesity I', color: 'text-orange-400' }
  if (bmi < 40) return { label: 'Obesity II', color: 'text-red-400' }
  return { label: 'Obesity III', color: 'text-red-500' }
}

interface BmiCalculatorFieldProps {
  value: string
  onChange: (val: string) => void
  onExtraFields: (fields: Record<string, string>) => void
  weightFromForm: string
  error: string
}

const BmiCalculatorField: React.FC<BmiCalculatorFieldProps> = ({
  value: _bmiValue,
  onChange,
  onExtraFields,
  weightFromForm,
  error,
}) => {
  const [heightFeet, setHeightFeet] = useState('5')
  const [heightInches, setHeightInches] = useState('6')
  const [localWeight, setLocalWeight] = useState(weightFromForm)

  // Sync if the weight field in the main form changes
  useEffect(() => {
    if (weightFromForm) setLocalWeight(weightFromForm)
  }, [weightFromForm])

  // Recalculate BMI whenever inputs change
  useEffect(() => {
    const ft = parseInt(heightFeet) || 0
    const inches = parseInt(heightInches) || 0
    const wt = parseFloat(localWeight) || 0
    const bmi = calculateBmi(ft, inches, wt)

    if (bmi !== null && bmi > 0 && bmi < 200) {
      onChange(String(bmi))
      onExtraFields({
        height_feet: String(ft),
        height_inches: String(inches),
        ...(weightFromForm === '' ? { weight: String(wt) } : {}),
      })
    } else {
      onChange('')
    }
  }, [heightFeet, heightInches, localWeight]) // eslint-disable-line react-hooks/exhaustive-deps

  const bmiNum = parseFloat(_bmiValue)
  const category = bmiNum ? getBmiCategory(bmiNum) : null

  const inputCls = `bg-slate-800/50 border-slate-600 text-slate-100 placeholder:text-slate-500 focus:border-[#00A79D] ${
    error ? 'border-red-500/60' : ''
  }`

  return (
    <div className="space-y-3">
      {/* Height row */}
      <div className="space-y-1.5">
        <Label className="text-xs text-slate-400">Height</Label>
        <div className="flex gap-2">
          <div className="flex-1">
            <div className="relative">
              <Input
                type="number"
                value={heightFeet}
                onChange={e => setHeightFeet(e.target.value)}
                min={1}
                max={8}
                placeholder="5"
                className={inputCls}
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-500">ft</span>
            </div>
          </div>
          <div className="flex-1">
            <div className="relative">
              <Input
                type="number"
                value={heightInches}
                onChange={e => setHeightInches(e.target.value)}
                min={0}
                max={11}
                placeholder="6"
                className={inputCls}
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-500">in</span>
            </div>
          </div>
        </div>
      </div>

      {/* Weight row — only show if weight is not already a separate field */}
      {weightFromForm === '' && (
        <div className="space-y-1.5">
          <Label className="text-xs text-slate-400">Weight</Label>
          <div className="relative">
            <Input
              type="number"
              value={localWeight}
              onChange={e => setLocalWeight(e.target.value)}
              min={1}
              placeholder="180"
              className={inputCls}
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-500">lbs</span>
          </div>
        </div>
      )}

      {/* Calculated BMI display */}
      {bmiNum > 0 && category && (
        <div className="flex items-center gap-3 p-3 bg-slate-800/60 border border-slate-700/60 rounded-lg">
          <div>
            <p className="text-xs text-slate-500">Calculated BMI</p>
            <p className="text-lg font-bold text-white">{bmiNum}</p>
          </div>
          <Badge variant="outline" className={`${category.color} border-current/30 text-xs`}>
            {category.label}
          </Badge>
        </div>
      )}
    </div>
  )
}

// ─────────────────────────────────────────────
// Single field renderer
// ─────────────────────────────────────────────

interface FieldRowProps {
  field: MissingField
  value: string
  onChange: (val: string) => void
  onExtraFields?: (fields: Record<string, string>) => void
  weightFromForm?: string
  error: string
}

const FieldRow: React.FC<FieldRowProps> = ({ field, value, onChange, onExtraFields, weightFromForm, error }) => {
  const meta = getFieldMeta(field.fieldId, field)
  const required = field.priority === 'required'

  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2">
        <Label className="text-sm font-medium text-slate-200">
          {meta.label}
          {required && <span className="text-red-400 ml-0.5">*</span>}
        </Label>
        <Badge
          variant="outline"
          className={
            field.priority === 'required'
              ? 'text-[10px] px-1.5 py-0 bg-red-500/20 text-red-400 border-red-500/30'
              : field.priority === 'recommended'
              ? 'text-[10px] px-1.5 py-0 bg-amber-500/20 text-amber-400 border-amber-500/30'
              : 'text-[10px] px-1.5 py-0 bg-slate-500/20 text-slate-400 border-slate-500/30'
          }
        >
          {field.priority}
        </Badge>
      </div>

      {meta.hint && (
        <p className="text-xs text-slate-500">{meta.hint}</p>
      )}
      {field.description && !meta.hint && (
        <p className="text-xs text-slate-500">{field.description}</p>
      )}

      {meta.inputType === 'bmi_calculator' && (
        <BmiCalculatorField
          value={value}
          onChange={onChange}
          onExtraFields={onExtraFields ?? (() => {})}
          weightFromForm={weightFromForm ?? ''}
          error={error}
        />
      )}

      {meta.inputType === 'textarea' && (
        <Textarea
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={`Enter ${meta.label.toLowerCase()}...`}
          rows={4}
          className={`min-h-[100px] bg-slate-800/50 border-slate-600 text-slate-100 placeholder:text-slate-500 focus:border-[#00A79D] focus:ring-[#00A79D]/20 ${
            error ? 'border-red-500/60' : ''
          }`}
        />
      )}

      {meta.inputType === 'number' && (
        <Input
          type="number"
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder="0"
          min={0}
          className={`bg-slate-800/50 border-slate-600 text-slate-100 placeholder:text-slate-500 focus:border-[#00A79D] ${
            error ? 'border-red-500/60' : ''
          }`}
        />
      )}

      {meta.inputType === 'select' && meta.options && (
        <Select value={value} onValueChange={onChange}>
          <SelectTrigger
            className={`bg-slate-800/50 border-slate-600 text-slate-100 focus:ring-[#00A79D]/20 ${
              error ? 'border-red-500/60' : ''
            } ${!value ? '[&>span]:text-slate-500' : ''}`}
          >
            <SelectValue placeholder="Select an option..." />
          </SelectTrigger>
          <SelectContent className="bg-slate-800 border-slate-600">
            {meta.options.map(opt => (
              <SelectItem key={opt.value} value={opt.value} className="text-slate-100 focus:bg-[#00A79D]/20">
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {meta.inputType === 'text' && (
        <Input
          type="text"
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={`Enter ${meta.label.toLowerCase()}...`}
          className={`bg-slate-800/50 border-slate-600 text-slate-100 placeholder:text-slate-500 focus:border-[#00A79D] ${
            error ? 'border-red-500/60' : ''
          }`}
        />
      )}

      {error && (
        <p className="text-xs text-red-400 flex items-center gap-1">
          <AlertCircle className="h-3 w-3 flex-shrink-0" />
          {error}
        </p>
      )}
    </div>
  )
}

// ─────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────

const SmartIntake: React.FC = () => {
  const { token } = useParams<{ token: string }>()

  const [pageState, setPageState] = useState<PageState>('loading')
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [intakeData, setIntakeData] = useState<ValidateData | null>(null)
  const [responses, setResponses] = useState<Record<string, string>>({})
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [submitError, setSubmitError] = useState<string>('')

  // Shipping & Billing Address state
  const [shippingAddress, setShippingAddress] = useState<AddressFields>({ ...initialAddressFields })
  const [billingAddress, setBillingAddress] = useState<AddressFields>({ ...initialAddressFields })
  const [billingSameAsShipping, setBillingSameAsShipping] = useState(true)
  const [addressError, setAddressError] = useState<string>('')

  // Validate token on mount
  useEffect(() => {
    if (!token) {
      setErrorMessage('No intake token provided.')
      setPageState('error')
      return
    }

    const init = async () => {
      try {
        const res = await fetch(apiUrl(`intakes/validate/${token}`))
        const body: { success: boolean; data?: ValidateData; message?: string } = await res.json()

        if (!body.success || !body.data) {
          setErrorMessage(body.message ?? 'Invalid or expired intake link.')
          setPageState('error')
          return
        }

        setIntakeData(body.data)
        // Initialise responses map
        const initResponses: Record<string, string> = {}
        ;(body.data.missingFields ?? []).forEach(f => {
          initResponses[f.fieldId] = ''
        })
        setResponses(initResponses)
        setPageState('form')

        // Fetch prefill data to pre-populate address fields
        try {
          const prefillResult = await getIntakePrefillData(token)
          if (prefillResult.success && prefillResult.data) {
            const pd = prefillResult.data
            if (pd.address_line_1 || pd.city || pd.state || pd.zip_code) {
              setShippingAddress(prev => ({
                ...prev,
                addressLine1: pd.address_line_1 || prev.addressLine1,
                addressLine2: pd.address_line_2 || prev.addressLine2,
                city: pd.city || prev.city,
                state: pd.state || prev.state,
                postalCode: pd.zip_code || prev.postalCode,
              }))
            }
          }
        } catch (prefillError) {
          console.error('Error fetching prefill data for SmartIntake:', prefillError)
          // Continue without prefill — non-blocking
        }
      } catch {
        setErrorMessage('Unable to load your intake. Please try again or contact your provider.')
        setPageState('error')
      }
    }

    init()
  }, [token])

  // Group fields by category, skipping lab fields (physician-ordered)
  const groupedFields = React.useMemo(() => {
    if (!intakeData) return []
    const labFieldIds = new Set(['labs', 'lab_results', 'lab_panel'])
    const nonLabFields = (intakeData.missingFields ?? []).filter(
      f => !labFieldIds.has(f.fieldId)
    )
    const grouped = nonLabFields.reduce<Record<string, MissingField[]>>((acc, field) => {
      const cat = field.category || 'other'
      if (!acc[cat]) acc[cat] = []
      acc[cat].push(field)
      return acc
    }, {})
    return Object.entries(grouped).sort(
      ([a], [b]) => (CATEGORY_ORDER[a] ?? 99) - (CATEGORY_ORDER[b] ?? 99)
    )
  }, [intakeData])

  const allFields = groupedFields.flatMap(([, fields]) => fields)
  const requiredFields = allFields.filter(f => f.priority === 'required')
  const filledCount = allFields.filter(f => responses[f.fieldId]?.trim()).length

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}
    requiredFields.forEach(f => {
      if (!responses[f.fieldId]?.trim()) {
        const meta = getFieldMeta(f.fieldId, f)
        newErrors[f.fieldId] = `${meta.label} is required`
      }
    })
    setErrors(newErrors)

    // Validate billing address (required), then shipping if different
    let addrValid = true
    if (!isAddressComplete(billingAddress)) {
      setAddressError('Please complete the billing address (street, city, state, and valid ZIP code).')
      addrValid = false
    } else if (!billingSameAsShipping && !isAddressComplete(shippingAddress)) {
      setAddressError('Please complete the shipping address (street, city, state, and valid ZIP code).')
      addrValid = false
    } else {
      setAddressError('')
    }

    return Object.keys(newErrors).length === 0 && addrValid
  }

  const handleSubmit = async () => {
    if (!validate()) {
      // Scroll to first error
      const el = document.querySelector('[data-field-error="true"]')
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      return
    }

    setPageState('submitting')
    setSubmitError('')

    try {
      const effectiveShipping = billingSameAsShipping ? billingAddress : shippingAddress
      const res = await fetch(apiUrl(`intakes/submit/${token}`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          responses,
          shipping_billing: {
            billingAddressLine1: billingAddress.addressLine1,
            billingAddressLine2: billingAddress.addressLine2,
            billingCity: billingAddress.city,
            billingState: billingAddress.state,
            billingPostalCode: billingAddress.postalCode,
            shippingAddressLine1: effectiveShipping.addressLine1,
            shippingAddressLine2: effectiveShipping.addressLine2,
            shippingCity: effectiveShipping.city,
            shippingState: effectiveShipping.state,
            shippingPostalCode: effectiveShipping.postalCode,
            billingSameAsShipping,
          },
        }),
      })
      const body = await res.json()
      if (!body.success) {
        setSubmitError(body.message ?? 'Submission failed. Please try again.')
        setPageState('form')
        return
      }
      setPageState('submitted')
    } catch {
      setSubmitError('Network error. Please check your connection and try again.')
      setPageState('form')
    }
  }

  // ─── Loading ───────────────────────────────
  if (pageState === 'loading') {
    return (
      <div className="min-h-screen bg-[#0A0F1A] flex items-center justify-center">
        <div className="text-center space-y-3">
          <Loader2 className="h-8 w-8 animate-spin text-[#00A79D] mx-auto" />
          <p className="text-slate-400 text-sm">Loading your intake form…</p>
        </div>
      </div>
    )
  }

  // ─── Error ────────────────────────────────
  if (pageState === 'error') {
    return (
      <div className="min-h-screen bg-[#0A0F1A] flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-slate-800/60 border-slate-700">
          <CardContent className="pt-8 pb-8 text-center space-y-4">
            <AlertCircle className="h-12 w-12 text-red-400 mx-auto" />
            <h1 className="text-xl font-semibold text-white">Intake Link Invalid</h1>
            <p className="text-slate-400 text-sm">{errorMessage}</p>
            <p className="text-slate-500 text-xs">
              Please contact your healthcare provider to request a new link.
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  // ─── Submitted ───────────────────────────
  if (pageState === 'submitted') {
    return (
      <div className="min-h-screen bg-[#0A0F1A] flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-slate-800/60 border-slate-700">
          <CardContent className="pt-8 pb-8 text-center space-y-4">
            <div className="w-16 h-16 bg-[#00A79D]/20 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle2 className="h-8 w-8 text-[#00A79D]" />
            </div>
            <h1 className="text-xl font-semibold text-white">Thank You!</h1>
            <p className="text-slate-300 text-sm">
              Your responses have been submitted. Your provider will review them and follow up with you shortly.
            </p>
            <div className="bg-[#00A79D]/10 border border-[#00A79D]/20 rounded-lg p-3">
              <p className="text-xs text-[#00A79D]">
                <strong>What's next:</strong> {intakeData?.providerName ?? 'Your provider'} will review your intake and contact you about next steps.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  // ─── Form ────────────────────────────────
  const data = intakeData!

  return (
    <div className="min-h-screen bg-[#0A0F1A] py-8 px-4">
      <div className="max-w-xl mx-auto space-y-6">

        {/* Header */}
        <div className="text-center space-y-1">
          <p className="text-xs uppercase tracking-widest text-slate-500 font-medium">
            {data.clinicName ?? 'Nimbus Providers'}
          </p>
          <h1 className="text-2xl font-bold text-white">
            {therapyLabel(data.therapyCategory)}
          </h1>
          <p className="text-sm text-slate-400">
            {data.providerName} needs a few more details to complete your assessment.
          </p>
        </div>

        {/* Progress */}
        <ProgressBar filled={filledCount} total={allFields.length} />

        {/* Submit error */}
        {submitError && (
          <div className="flex items-start gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-sm text-red-400">
            <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <span>{submitError}</span>
          </div>
        )}

        {/* Field groups */}
        {groupedFields.map(([category, fields]) => {
          const catMeta = CATEGORY_META[category]
          const Icon = catMeta?.icon ?? ClipboardList
          const catLabel = catMeta?.label ?? category.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())

          return (
            <Card key={category} className="bg-slate-800/40 border-slate-700/60">
              <CardContent className="p-5 space-y-5">
                {/* Category header */}
                <div className="flex items-center gap-2 pb-1 border-b border-slate-700/50">
                  <div className="w-7 h-7 rounded-lg bg-[#00A79D]/10 flex items-center justify-center">
                    <Icon className="h-3.5 w-3.5 text-[#00A79D]" />
                  </div>
                  <span className="text-sm font-semibold text-slate-200">{catLabel}</span>
                </div>

                {/* Fields */}
                {fields.map(field => (
                  <div
                    key={field.fieldId}
                    data-field-error={!!errors[field.fieldId]}
                  >
                    <FieldRow
                      field={field}
                      value={responses[field.fieldId] ?? ''}
                      onChange={val => {
                        setResponses(prev => ({ ...prev, [field.fieldId]: val }))
                        if (errors[field.fieldId]) {
                          setErrors(prev => ({ ...prev, [field.fieldId]: '' }))
                        }
                      }}
                      onExtraFields={extra => {
                        setResponses(prev => ({ ...prev, ...extra }))
                      }}
                      weightFromForm={field.fieldId === 'bmi' ? (responses['weight'] ?? '') : undefined}
                      error={errors[field.fieldId] ?? ''}
                    />
                  </div>
                ))}
              </CardContent>
            </Card>
          )
        })}

        {/* Shipping & Billing Address */}
        <Card className="bg-slate-800/40 border-slate-700/60">
          <CardContent className="p-5 space-y-5">
            <div className="flex items-center gap-2 pb-1 border-b border-slate-700/50">
              <div className="w-7 h-7 rounded-lg bg-[#00A79D]/10 flex items-center justify-center">
                <Package className="h-3.5 w-3.5 text-[#00A79D]" />
              </div>
              <span className="text-sm font-semibold text-slate-200">Shipping & Billing Address</span>
              <Badge
                variant="outline"
                className="text-[10px] px-1.5 py-0 bg-red-500/20 text-red-400 border-red-500/30"
              >
                required
              </Badge>
            </div>

            <ShippingBillingAddressStep
              shippingAddress={shippingAddress}
              billingAddress={billingAddress}
              billingSameAsShipping={billingSameAsShipping}
              onShippingChange={(field, value) => {
                setShippingAddress(prev => ({ ...prev, [field]: value }))
                if (addressError) setAddressError('')
              }}
              onBillingChange={(field, value) => {
                setBillingAddress(prev => ({ ...prev, [field]: value }))
                if (addressError) setAddressError('')
              }}
              onBillingSameToggle={(value) => {
                setBillingSameAsShipping(value)
                if (addressError) setAddressError('')
              }}
              variant="dark"
            />

            {addressError && (
              <p className="text-xs text-red-400 flex items-center gap-1">
                <AlertCircle className="h-3 w-3 flex-shrink-0" />
                {addressError}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Submit */}
        <div className="space-y-3 pb-8">
          <Button
            onClick={handleSubmit}
            disabled={pageState === 'submitting'}
            className="w-full h-12 bg-[#00A79D] hover:bg-[#00A79D]/90 text-white font-semibold text-base"
          >
            {pageState === 'submitting' ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Submitting…
              </>
            ) : (
              <>
                <CheckCircle2 className="mr-2 h-4 w-4" />
                Submit My Responses
              </>
            )}
          </Button>

          <div className="flex items-center justify-center gap-1.5 text-xs text-slate-500">
            <Shield className="h-3 w-3" />
            Your responses are secure and HIPAA-compliant
          </div>

          {requiredFields.length > 0 && (
            <p className="text-center text-xs text-slate-500">
              Fields marked <span className="text-red-400">*</span> are required
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

export default SmartIntake
